import React, {useState} from 'react';
import Image from 'next/image';
import {getLevelStyle, companyLogoSrc, formatNumber} from '../../lib/resumeUtils';
import {LikesIcon, ViewsIcon, VerifiedBadge} from './ResumeIcons';

function CompanyLogoStack({companyMetas}) {
  if (!companyMetas || companyMetas.length === 0) return null;

  const totalLogos = companyMetas.length;
  const firstRowCount = Math.min(totalLogos, 4);
  let secondRowCount; let
    remainingCount;

  if (totalLogos <= 6) {
    secondRowCount = totalLogos - firstRowCount;
    remainingCount = 0;
  } else {
    secondRowCount = 1;
    remainingCount = totalLogos - firstRowCount - secondRowCount;
  }

  const renderLogo = (company, index) => {
    if (!company.domain) return null;
    return (
      <div key={index} className="relative group/logo transition-all duration-200">
        <div
          className="absolute opacity-0 group-hover/logo:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200"
          style={{
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '4px',
            padding: '2px 6px',
            backgroundColor: '#1f2937',
            color: 'white',
            fontSize: '10px',
            borderRadius: '2px',
          }}
        >
          {company.name}
        </div>
        <div
          className="absolute inset-0 rounded-lg opacity-30"
          style={{
            backgroundColor: '#9ca3af',
            transform: 'translateY(4px)',
            filter: 'blur(2px)',
          }}
        />
        <Image
          src={companyLogoSrc(company.domain)}
          alt={`${company.name} logo`}
          width={35}
          height={35}
          style={{
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
            border: '3px solid #fafafa',
            position: 'relative',
          }}
        />
      </div>
    );
  };

  return (
    <div className="group-hover:scale-105 flex flex-col gap-3 mt-4 transition-all duration-300">
      <div className="flex justify-center gap-3">
        {companyMetas.slice(0, firstRowCount).map(renderLogo)}
      </div>

      {(secondRowCount > 0 || remainingCount > 0) && (
        <div className="flex justify-center gap-3">
          {companyMetas.slice(firstRowCount, firstRowCount + secondRowCount).map(renderLogo)}
          {remainingCount > 1 && (
            <div className="relative group/logo transition-all duration-200">
              <div
                className="absolute opacity-0 group-hover/logo:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200"
                style={{
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginBottom: '4px',
                  padding: '2px 6px',
                  backgroundColor: '#1f2937',
                  color: 'white',
                  fontSize: '10px',
                  borderRadius: '2px',
                }}
              >
                {remainingCount}
                {' '}
                more
              </div>
              <div
                className="absolute inset-0 rounded-lg opacity-30"
                style={{
                  backgroundColor: '#9ca3af',
                  transform: 'translateY(4px)',
                  filter: 'blur(2px)',
                }}
              />
              <div className="flex justify-center items-center w-9 h-9 rounded-lg bg-blue-600 relative shadow-md" style={{border: '3px solid #fafafa'}}>
                <span className="text-white font-medium" style={{fontSize: '16px'}}>
                  +
                  {remainingCount}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const LEVEL_BG_COLORS = {
  'bg-blue-600': {
    bg: '#2563eb', bgLight: '#eff6ff', border: '#1d4ed8', badgeBg: '#dbeafe', badgeText: '#1e40af', badgeBorder: '#93c5fd',
  },
  'bg-red-500': {
    bg: '#ef4444', bgLight: '#fef2f2', border: '#b91c1c', badgeBg: '#fee2e2', badgeText: '#991b1b', badgeBorder: '#fca5a5',
  },
  'bg-green-600': {
    bg: '#16a34a', bgLight: '#f0fdf4', border: '#15803d', badgeBg: '#dcfce7', badgeText: '#166534', badgeBorder: '#86efac',
  },
  'bg-pink-600': {
    bg: '#db2777', bgLight: '#fdf2f8', border: '#db2777', badgeBg: '#fce7f3', badgeText: '#9d174d', badgeBorder: '#f9a8d4',
  },
  'bg-yellow-600': {
    bg: '#ca8a04', bgLight: '#fefce8', border: '#a16207', badgeBg: '#fef9c3', badgeText: '#854d0e', badgeBorder: '#fde68a',
  },
  'bg-teal-600': {
    bg: '#0d9488', bgLight: '#f0fdfa', border: '#0f766e', badgeBg: '#ccfbf1', badgeText: '#115e59', badgeBorder: '#99f6e4',
  },
  'bg-gray-600': {
    bg: '#4b5563', bgLight: '#f9fafb', border: '#4b5563', badgeBg: '#f3f4f6', badgeText: '#374151', badgeBorder: '#d1d5db',
  },
};

// The preview is the anonymized render, shown blurred and greyed so the card
// reads as a resume without any of its text being legible.
const PREVIEW_STYLE = {filter: 'blur(3px) grayscale(1)', opacity: 0.6};

export default function ResumeCard({resume, position, onViewResume}) {
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewUrl = !previewFailed && resume.thumbnailUrl;
  const likeCount = resume.likeCount || 0;
  const levelStyle = getLevelStyle(resume.level);
  const colors = LEVEL_BG_COLORS[levelStyle.color.bg] || LEVEL_BG_COLORS['bg-blue-600'];

  const handleClick = () => {
    if (onViewResume) {
      onViewResume(resume.username, {resumeId: resume.resumeId, position});
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="group flex flex-col cursor-pointer transition-transform duration-300 hover:scale-105"
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
    >
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Top area - resume placeholder with company logos */}
        <div
          className="h-36 overflow-hidden relative border-b border-gray-200"
          style={{backgroundColor: colors.bgLight}}
        >
          <div className="relative flex justify-center items-start mt-4">
            {previewUrl ? (
              <Image
                src={previewUrl}
                alt="Blurred preview of the resume"
                width={260}
                height={336}
                draggable={false}
                onError={() => setPreviewFailed(true)}
                style={{
                  borderRadius: '8px',
                  border: `2px solid ${colors.border}`,
                  width: '90%',
                  height: 'auto',
                  ...PREVIEW_STYLE,
                }}
              />
            ) : (
              <Image
                src="/resume-placeholder.svg"
                alt="resume"
                width={260}
                height={260}
                style={{borderRadius: '8px', border: `2px solid ${colors.border}`, width: '90%'}}
              />
            )}
          </div>

          {/* Level badge */}
          <span
            className="absolute top-2 left-2 rounded-full flex justify-center items-center h-5 text-xs font-medium"
            style={{
              padding: '0.5px 12px',
              backgroundColor: colors.badgeBg,
              color: colors.badgeText,
              border: `1px solid ${colors.badgeBorder}`,
            }}
          >
            {levelStyle.text.replace(/\b\w+/g, (w) => w.charAt(0) + w.slice(1).toLowerCase())}
          </span>

          <div className="absolute inset-0 flex justify-center items-center">
            <CompanyLogoStack companyMetas={resume.companyMetas} />
          </div>
        </div>

        {/* Bottom bar - role info */}
        <div className="h-14 px-3 py-2 flex justify-between items-center bg-white">
          <div className="flex flex-row gap-2 items-center">
            {resume.verified && <VerifiedBadge size={32} />}
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium text-gray-700">{resume.roleName}</span>
              <span className="text-xs text-gray-500 overflow-hidden overflow-ellipsis whitespace-nowrap" style={{maxWidth: '200px'}}>
                {resume.educations?.map((e) => e.schoolName).join(', ')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Below card - username, likes, views */}
      <div className="pt-1 h-6 flex justify-between items-center">
        <div className="flex gap-2 items-center overflow-hidden" style={{maxWidth: '60%'}}>
          {resume.userImage ? (
            <Image
              src={resume.userImage}
              alt={resume.username}
              width={18}
              height={18}
              style={{borderRadius: '50%'}}
            />
          ) : (
            <div
              className="w-4 h-4 rounded-full bg-gray-500 flex items-center justify-center text-white flex-shrink-0"
              style={{
                fontSize: '10px', fontWeight: 500, width: '18px', height: '18px',
              }}
            >
              {(resume.username || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-xs font-medium text-gray-700 overflow-hidden overflow-ellipsis whitespace-nowrap">{resume.username}</span>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1">
            <LikesIcon />
            <span className="text-xs text-gray-500">
              {likeCount}
              <span className="sr-only"> likes</span>
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ViewsIcon />
            <span className="text-xs text-gray-500">
              {formatNumber(resume.viewCount)}
              <span className="sr-only"> views</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

